import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import {
  fetchApiNotificationSettings,
  readNotificationSettings,
  type NotificationSettings,
  writeApiNotificationSettings,
  writeNotificationSettings,
} from 'src/util/notificationSettings';
import { useAPI } from 'src/util/use-api';

type NotificationSettingsContextValue = {
  settings: NotificationSettings;
  applyNotificationSettings: (nextSettings: NotificationSettings) => void;
  refetchNotificationSettings: () => void;
};

const NotificationSettingsContext =
  createContext<NotificationSettingsContextValue | null>(null);

export const NOTIFICATION_SETTINGS_QUERY_KEY = ['notification-settings'];

const useNotificationSettingsValue = (): NotificationSettingsContextValue => {
  const { API, authHeaderSet } = useAPI();
  const queryClient = useQueryClient();
  const [settings, setSettings] = useState<NotificationSettings>(
    readNotificationSettings
  );

  const { data: apiSettings, dataUpdatedAt } = useQuery({
    queryKey: NOTIFICATION_SETTINGS_QUERY_KEY,

    queryFn: async () => {
      return fetchApiNotificationSettings(API.activitiesAPI);
    },

    enabled: authHeaderSet,
    staleTime: 60_000,
  });

  // Runs after every successful fetch (like the v4 query onSuccess callback).
  // Data already cached when this provider mounted was not fetched by it, and
  // may be older than settings applied locally since, so it is skipped.
  const cachedAtMountRef = useRef(dataUpdatedAt);
  useEffect(() => {
    if (!apiSettings || dataUpdatedAt === cachedAtMountRef.current) return;
    setSettings(writeApiNotificationSettings(apiSettings));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataUpdatedAt]);

  const applyNotificationSettings = useCallback(
    (nextSettings: NotificationSettings) => {
      writeNotificationSettings(nextSettings);
      setSettings(nextSettings);
    },
    []
  );

  const refetchNotificationSettings = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: NOTIFICATION_SETTINGS_QUERY_KEY,
    });
  }, [queryClient]);

  return {
    settings,
    applyNotificationSettings,
    refetchNotificationSettings,
  };
};

export const NotificationSettingsProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const value = useNotificationSettingsValue();

  return createElement(
    NotificationSettingsContext.Provider,
    { value },
    children
  );
};

const useNotificationSettingsContext = () => {
  const contextValue = useContext(NotificationSettingsContext);

  if (!contextValue) {
    throw new Error(
      'useNotificationSettings must be used inside NotificationSettingsProvider'
    );
  }

  return contextValue;
};

export const useNotificationSettings = (): NotificationSettings => {
  return useNotificationSettingsContext().settings;
};

export const useNotificationSettingsActions = () => {
  const { applyNotificationSettings, refetchNotificationSettings } =
    useNotificationSettingsContext();

  return { applyNotificationSettings, refetchNotificationSettings };
};
